<template>
  <div>
    <template v-if="$route.name === 'chapters.index'">
      <ChapterFilters
        @search="handleSearch"
        @add="navigateToAdd"
      />

      <ChapterTable
        :chapters="chapters"
        :loading="isLoading"
        @delete-chapter="openDeleteDialog"
      />

      <ChapterDeleteDialog
        ref="deleteDialog"
        @delete="deleteChapter"
      />
    </template>

    <router-view></router-view>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useI18n } from 'vue-i18n';
import ChapterTable from '@/components/features/chapters/ChapterTable.vue';
import ChapterFilters from '@/components/features/chapters/ChapterFilters.vue';
import ChapterDeleteDialog from '@/components/features/chapters/dialogs/ChapterDeleteDialog.vue';
import api from '@/api';

const { t, locale } = useI18n();
const router = useRouter();
const toast = useToast();

const deleteDialog = ref(null);
const chapters = ref([]);
const isLoading = ref(false);
const isDeleting = ref(false);
const searchTerm = ref('');

function extractApiMessage(error, fallbackKey = 'errors.generic') {
  const data = error?.response?.data;

  if (data?.message) {
    if (typeof data.message === 'object' && data.message !== null) {
      const loc = (data.locale || locale.value || 'en').toLowerCase().startsWith('ar') ? 'ar' : 'en';
      if (typeof data.message[loc] === 'string') return data.message[loc];
    }

    if (typeof data.message === 'string') return data.message;
  }

  if (error?.response?.statusText) return error.response.statusText;

  return t(fallbackKey);
}

function notifyError(error, fallbackKey = 'errors.generic') {
  const s = error?.response?.status;

  if (s === 401) return toast.error(t('auth.unauthorized', 'Unauthorized'));
  if (s === 403) return toast.error(t('auth.forbidden', 'Forbidden'));

  return toast.error(extractApiMessage(error, fallbackKey));
}

const fetchChapters = async () => {
  isLoading.value = true;

  try {
    const response = await api.get('/chapters', {
      params: {
        search: searchTerm.value,
        with_subjects_count: true
      }
    });

    chapters.value = response.data?.data ?? [];
  } catch (error) {
    notifyError(error, 'chapters.fetchErrorChapters');
    console.error('Error fetching chapters:', error);
  } finally {
    isLoading.value = false;
  }
};

const handleSearch = (term) => {
  searchTerm.value = term;
  fetchChapters();
};

const openDeleteDialog = (chapter) => {
  if (!chapter?.id || isDeleting.value) return;
  deleteDialog.value?.open(chapter);
};

const deleteChapter = async (id) => {
  if (!id || isDeleting.value) return;

  isDeleting.value = true;

  try {
    await api.delete(`/chapters/${id}`);
    toast.success(t('chapters.deleteSuccess'));
    await fetchChapters();
  } catch (error) {
    if (error?.response?.status === 403) {
      toast.error(t('auth.forbidden', 'Forbidden'));
    } else {
      notifyError(error, 'chapters.deleteError');
    }
    console.error('Error deleting chapter:', error);
  } finally {
    isDeleting.value = false;
  }
};

const navigateToAdd = () => {
  router.push({ name: 'chapters.create' });
};

onMounted(() => {
  fetchChapters();
});
</script>